import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';

const QUESTIONS = [
  {
    q: 'Who actually holds my money?',
    a: 'HireBroker holds it. You pay the full brokerage fee to HireBroker through the mobile app — not to the realtor. HireBroker is the merchant that receives the payment. The assigned realtor does not receive the remaining fee until you accept a property.',
  },
  {
    q: 'Is the money kept separately from operational funds?',
    a: 'HireBroker holds your brokerage fee for the rental hunt. From that fee, HireBroker may pay the assigned realtor’s transport costs for inspections. The remaining balance stays with HireBroker until you accept a property, or until a rematch or refund is completed. This is a HireBroker-held rental hunting fee. It is not a licensed third-party escrow account, and it is not a bank client-money trust for land or purchase funds.',
  },
  {
    q: 'What happens if the agent fails to deliver?',
    a: 'Tell HireBroker. After we verify the issue with the previous realtor, we can assign another agent, realtor, or broker to continue the hunt. If a rematch is not the right next step, HireBroker refunds the remaining balance after any transport already paid from the fee.',
  },
  {
    q: 'What happens if I reject the property?',
    a: 'If you do not want the properties shown, HireBroker reviews what happened. We can assign another realtor after verifying the previous assignment, or refund the remaining balance. The realtor is not paid the remaining brokerage fee unless you accept a property.',
  },
  {
    q: 'How long does a refund take?',
    a: 'HireBroker first verifies the issue. After a refund of the remaining balance is approved, it is sent to the original payment method. Mobile money and card refunds usually arrive within 3–7 business days, depending on the provider. Transport already paid from the fee is not part of the remaining balance.',
  },
  {
    q: 'What happens in a dispute?',
    a: 'Contact HireBroker with the details. We verify the issue involving the previous realtor, then either assign another realtor or refund the remaining balance. Email support@hirebroker.co or call +256 774 509 859. Unresolved disputes follow the Terms of Service under Uganda law.',
  },
];

const STEPS = [
  {
    tag: '01',
    title: 'Request a rental in the app',
    text: 'Open the HireBroker app, say where you want the rental, and pick the day you want to inspect.',
  },
  {
    tag: '02',
    title: 'Pay the full brokerage fee to HireBroker',
    text: 'You pay HireBroker, not the realtor. HireBroker holds that fee for the rental hunt.',
  },
  {
    tag: '03',
    title: 'Get assigned a realtor',
    text: 'HireBroker assigns a property realtor. Transport for inspections is covered from the fee you paid.',
  },
  {
    tag: '04',
    title: 'Inspect, then accept or raise an issue',
    text: 'If you accept the property, HireBroker pays the realtor the remaining fee. If you have an issue with the realtor or do not want the properties shown, HireBroker verifies the issue and either assigns another realtor or refunds the balance.',
  },
];

const HowFeesWork = () => {
  return (
    <div className="min-h-screen bg-white text-neutral-900 selection:bg-red-50 selection:text-red-900">
      <Header />

      <main className="pt-24">
        <section className="bg-white tracking-tight">
          <div className="max-w-6xl mx-auto px-6 pt-16 pb-20">
            <p className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold mb-4">
              Rental hunting only
            </p>
            <h1 className="text-4xl sm:text-6xl font-light text-neutral-950 leading-[1.1] mb-6 max-w-4xl">
              How HireBroker holds your <span className="font-serif italic text-neutral-500">brokerage fee.</span>
            </h1>
            <p className="text-lg text-neutral-500 font-light leading-relaxed max-w-2xl">
              For rental hunting, you pay HireBroker the full brokerage fee in the app. HireBroker holds it, covers the realtor’s transport from that fee, and pays the realtor only after you accept a property. HireBroker does not hold money for land transactions.
            </p>
          </div>
        </section>

        <section className="bg-neutral-50 border-y border-neutral-200/60 py-20">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-light text-neutral-950 tracking-tight mb-12">The rental hunt, in order</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {STEPS.map((step) => (
                <div key={step.tag} className="bg-white p-6 border border-neutral-200/60">
                  <span className="text-xs font-mono font-bold text-red-600">{step.tag}</span>
                  <h3 className="mt-3 font-medium text-neutral-950">{step.title}</h3>
                  <p className="mt-2 text-sm text-neutral-500 font-light leading-relaxed">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-24">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-light text-neutral-950 tracking-tight mb-4">Your money, in plain language</h2>
            <p className="text-neutral-500 font-light mb-12 max-w-2xl">
              When we say HireBroker holds the fee, this is what that means.
            </p>
            <div className="space-y-10">
              {QUESTIONS.map((item) => (
                <div key={item.q} className="border-l-2 border-red-600 pl-6">
                  <h3 className="text-lg font-medium text-neutral-950 mb-2">{item.q}</h3>
                  <p className="text-neutral-500 font-light leading-relaxed max-w-3xl">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-neutral-50 border-y border-neutral-200/60 py-20">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-light text-neutral-950 tracking-tight mb-8">What this fee is not</h2>
            <ul className="space-y-4 text-neutral-500 font-light leading-relaxed max-w-3xl">
              <li>It is not rent, a security deposit, or the purchase price of a property.</li>
              <li>It is not used for land sales or land purchases. HireBroker does not hold money for land transactions.</li>
              <li>It is not a licensed third-party bank escrow account. HireBroker holds the rental hunting brokerage fee and pays the realtor after you accept.</li>
              <li>The 10,000 UGX listing fee for publishing an asset is a separate broadcast fee. It is not this hunting fee.</li>
            </ul>
          </div>
        </section>

        <section className="bg-neutral-950 text-white py-24">
          <div className="max-w-4xl mx-auto px-6 text-center space-y-6">
            <h2 className="text-3xl font-light tracking-tight">Start a rental hunt in the app</h2>
            <p className="text-neutral-400 font-light max-w-xl mx-auto">
              Set the area, pick an inspection day, and pay HireBroker the brokerage fee. Questions about a hold, rematch, or refund go to support.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-2">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-6 py-3 bg-red-600 text-white text-sm font-medium hover:bg-red-700"
              >
                Contact support
              </Link>
              <Link
                to="/terms"
                className="inline-flex items-center justify-center px-6 py-3 border border-neutral-800 text-neutral-300 text-sm font-medium hover:bg-neutral-900"
              >
                Read the terms
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HowFeesWork;
